

import { useEffect, useMemo, useState } from 'react';
import { Button } from '@repo/ui/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@repo/ui/components/ui/card';
import { Skeleton } from '@repo/ui/components/ui/skeleton';
import { useSaveEpaysageBase } from '@repo/ui/hooks/api/epaysages';
import { EpaysageBaseSaveInput, Epaysage } from '@repo/types';
import { AlertCircle } from 'lucide-react';
import { Alert } from '@repo/ui/components/ui/alert';
import { toast } from 'sonner';
import { EpaysageFieldRenderer } from './field-renderer';
import { epaysageFields } from '@repo/field-configs';
import { useInventoryData } from '@repo/ui/hooks/use-inventory-data';

export interface EpaysageAdvancedFieldsProps {
  epaysage: Epaysage;
  epaysageId: number;
  inventaireId?: number;
  onSuccess?: () => void;
}

type FieldConfig = (typeof epaysageFields)[keyof typeof epaysageFields] & { hidden?: boolean };

export function EpaysageAdvancedFields({ epaysage, epaysageId, inventaireId, onSuccess }: EpaysageAdvancedFieldsProps) {
  const [formData, setFormData] = useState<Record<string, any>>(() => mapEpaysageToFormState(epaysage));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [generalError, setGeneralError] = useState<string | null>(null);

  // Load inventory data (champignons, nuisibles) for freeforms
  const {
    champignons,
    nuisibles,
    isLoading: isLoadingInventoryData,
    hasError: hasInventoryDataError,
  } = useInventoryData(true);

  useEffect(() => {
    setFormData(mapEpaysageToFormState(epaysage));
    console.log({ formData });
    setErrors({});
    setGeneralError(null);
  }, [epaysage]);

  const saveMutation = useSaveEpaysageBase({
    onSuccess: () => {
      setGeneralError(null);
      toast.success('Modifications enregistrées avec succès');
      onSuccess?.();
    },
    onError: (error) => {
      const message = getErrorMessage(error);
      setGeneralError(message);
      toast.error(message);
    },
  });

  const groupedFields = useMemo(() => groupFieldsByFieldset(epaysageFields), []);

  const handleFieldChange = (key: string, value: any) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => {
      if (!prev[key]) {
        return prev;
      }
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const handleSubmit = () => {
    setGeneralError(null);
    const payload = mapAdvancedFieldsToPayload(formData, { epaysageId, inventaireId, baseEpaysage: epaysage });
    saveMutation.mutate(payload);
  };

  if (!epaysage) {
    return (
      <Card>
        <CardContent className="p-6">
          <Skeleton className="h-10 w-1/3 mb-4" />
          <Skeleton className="h-24 w-full" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="space-y-4">
      <CardHeader>
        <CardTitle>Caractéristiques détaillées</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {generalError && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <div>
              <h4 className="font-medium">Erreur</h4>
              <p className="text-sm">{generalError}</p>
            </div>
          </Alert>
        )}

        {/* Inventory Data Loading Alert */}
        {isLoadingInventoryData && (
          <Alert className="border-blue-200 bg-blue-50">
            <AlertCircle className="h-4 w-4 text-blue-600" />
            <div className="text-blue-800">
              Chargement des données d'inventaire (champignons, nuisibles)...
            </div>
          </Alert>
        )}

        {/* Inventory Data Error Alert */}
        {hasInventoryDataError && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <div>
              <h4 className="font-medium">Erreur</h4>
              <p className="text-sm">Erreur lors du chargement des données d'inventaire</p>
            </div>
          </Alert>
        )}

        {Object.entries(groupedFields).map(([fieldset, fields]) => (
          <div key={fieldset} className="space-y-4">
            <div className="border-b pb-2">
              <p className="text-sm font-medium text-muted-foreground">{fieldset}</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {fields.map(([key, config]) => (
                <EpaysageFieldRenderer
                  key={key}
                  fieldKey={key}
                  fieldConfig={config}
                  value={formData[key]}
                  error={errors[key]}
                  onChange={handleFieldChange}
                  formData={formData}
                  champignons={champignons}
                  nuisibles={nuisibles}
                />
              ))}
            </div>
          </div>
        ))}

        <div className="flex justify-end gap-3">
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={saveMutation.isPending}
          >
            {saveMutation.isPending ? 'Enregistrement...' : 'Enregistrer les modifications'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function groupFieldsByFieldset(fieldsConfig: Record<string, FieldConfig>) {
  const grouped: Record<string, Array<[string, FieldConfig]>> = {};
  Object.entries(fieldsConfig).forEach(([key, config]) => {
    const groupKey = config.fieldset || 'Autres';
    if (config.hidden) {
      return;
    }
    if (!grouped[groupKey]) {
      grouped[groupKey] = [];
    }
    grouped[groupKey].push([key, config]);
  });
  return grouped;
}

function mapEpaysageToFormState(epaysage: Epaysage): Record<string, any> {
  const formState: Record<string, any> = {};
  Object.entries(epaysageFields).forEach(([key, config]) => {
    const serverKey = config.server || key;
    formState[key] = (epaysage as any)[serverKey] ?? null;

    // Also map freeform fields
    if ((config as any).freeforms) {
      (config as any).freeforms.forEach((ff: any) => {
        const ffServerKey = ff.server || `${key}_${ff.id}`;
        formState[ffServerKey] = (epaysage as any)[ffServerKey] ?? null;
      });
    }
  });
  return formState;
}

function mapAdvancedFieldsToPayload(
  formState: Record<string, any>,
  options: { epaysageId: number; inventaireId?: number; baseEpaysage: Epaysage }
): EpaysageBaseSaveInput {

  const epaysageData: Record<string, any> = {
    coord: options.baseEpaysage.coord ?? null,
    address: options.baseEpaysage.address ?? null,
    ville: options.baseEpaysage.ville ?? null,
    pays: options.baseEpaysage.pays ?? null,
    area: options.baseEpaysage.area ?? null,
    type_espace: options.baseEpaysage.type_espace ?? null,
    type_espace_autre: options.baseEpaysage.type_espace_autre ?? null,
    code_site: options.baseEpaysage.code_site ?? null,
    num_sujet: options.baseEpaysage.num_sujet ?? null,
    age_estime: options.baseEpaysage.age_estime ?? null,
    milieu: formState['milieu'] ?? null,
    milieu_other: formState['milieu_other'] ?? null,
    sol: formState['sol'] ?? null,
    sol_other: formState['sol_other'] ?? null,
    taux_freq: formState['taux-de-frquentation'] ?? null,
    accessibilite: formState['accessibilite'] ?? null,
    accessibilite_other: formState['accessibilite_other'] ?? null,
    type_passage: formState['type-de-passage'] ?? null,
    type_passage_other: formState['type_passage_other'] ?? null,
    risque_general: formState['risque-general'] ?? null,
    commentaire: formState['commentaire'] ?? null,
    domaine: formState['domaine'] ?? null,
    nuissance: formState['nuisances-constatees'] ?? null,
    nuissance_other: formState['nuissance_other'] ?? null,
    proximite: formState['proximite-directe'] ?? null,
    proximite_other: formState['proximite-directe_other'] ?? null,
    location_index: Number(formState['valeur-intrinseque-indice-situation']) ?? null,
    situation: formState['valeur-intrinseque-indice-situation'] ?? null,

  };

  // Add all freeform fields from epaysageFields config
  Object.entries(epaysageFields).forEach(([key, config]) => {
    if ((config as any).freeforms) {
      (config as any).freeforms.forEach((ff: any) => {
        const ffServerKey = ff.server || `${key}_${ff.id}`;
        if (formState[ffServerKey] !== undefined) {
          epaysageData[ffServerKey] = formState[ffServerKey] ?? null;
        }
      });
    }
  });



  return {
    mode: 'UPDATE',
    epaysage_id: options.epaysageId,
    inventaire_id: options.inventaireId,
    epaysage: epaysageData,
  };
}

function getErrorMessage(error: unknown): string {
  if (!error) return 'Erreur lors de la sauvegarde';
  const maybeError = error as { response?: { data?: { message?: string } }; message?: string };
  return (
    maybeError.response?.data?.message ||
    maybeError.message ||
    'Erreur lors de la sauvegarde'
  );
}

